import {
  ATC_CLASS_LABEL,
  ATC_STATUS_LABEL,
  atcStatusSchema,
  type AtcPersonList,
  type AtcStandard,
  type AtcStandardList,
  type AtcStatus,
  type AtcVersion,
  type AtcInput,
  type NodeType,
  type RunLedgerRow,
  type TreeNode,
} from '@capsio/contracts';
import { useEffect, useState, type ReactNode } from 'react';
import { api, messageOf } from '../api-client.js';
import { BusyButton } from '../components/Field.js';
import { owningUnitOf, pathNamesOf, statusOf, ROOT_ID, type TreeIndex } from './node-tree-path.js';
import { AtcBaselineBlock } from './AtcBaselineBlock.js';
import { AtcVersionHistory } from './AtcVersionHistory.js';
import {
  ActionPackagePanel,
  BlueprintPanel,
  CardBadges,
  DataLinksPanel,
  DefinitionPanel,
  MATURITY_LABEL,
  OverviewPanel,
  PeoplePanel,
  RegistrationPanel,
  RunsPanel,
  SopPanel,
  TRIGGER_LABEL,
} from './AtcCardPanels.js';
import { atcClassOf } from './node-tree-path.js';
import { Field } from '../components/Field.js';

/**
 * ATC 这一页：**看、改、新建都是它**，而且**卡页不是模型的一个视图——卡页就是那份定义本身**。
 *
 * 点开任意一张 ATC，屏幕上呈现的就是这张卡的完整定义：人据此知道怎么做、边界在哪、
 * 谁负责；AI 据此知道能不能自己跑、跑到哪必须停；监督据此判断该不该放行；
 * 记录据此追溯这一次按哪一版发生。**缺什么一眼看得出、缺在哪一格也看得出**——
 * 三态互斥：零态（有表有列无数据，照实说「还没填」「0」）、无源（无表无列，不占格）、
 * 待建（模型已确认需要尚未建，统一标记）。
 *
 * 页面上每一个可见的数都能指到「表.列」或视图：身份与归属在 `atc` 本体，
 * 四段与边界在 `atc_version`（版本档），人员在 `atc_person`，输入在
 * `atc_material_input`，台账在 `taskrun`，计量统计全部由台账派生（统计，非权威事实）。
 *
 * 看、改、新建同一个组件，subject 决定它是哪种状态：
 * - `saved`：树上真有这条记录。改完按保存。
 * - `draft`：**还没落库**。点「创建 ATC」进到这一页，填完才 addNode。
 *
 * 草稿刻意不先落库再编辑：ATC 只能退役不能删（执行记录要引用它依据的那一版），
 * 一进来就建记录的话，误点一次就永久留下一条空卡，只能退役、消不掉。
 */

export type AtcSubject =
  | { kind: 'saved'; node: TreeNode }
  | {
      kind: 'draft';
      /** 建在哪儿。null 表示挂在企业下（全公司通用的标准）。 */
      parentId: string | null;
      /**
       * 这个位置允许建的资产类型，已经由 canPlaceUnder 过滤过。
       *
       * 目前只有 ATC 一种，所以实际上永远是一个；给的是数组而不是单个，
       * 因为「以后还有别的资产」这件事是设计里写着的，到时候这儿只多一个下拉。
       */
      options: NodeType[];
    };

/*
 * 九个分区，名称是裁定的，逐字照此。
 *
 * 前身是六个页签（工作台 / 运行记录 / 蓝图 / 溯源 / 附件 / 变更记录）。
 * 「工作台」一词产品内不出现（已裁）；旧页签的内容各有归属：
 * 四段与基线 → 分区 1/3，运行记录 → 分区 8（扩成全列），蓝图 → 分区 9，
 * 溯源（归属） → 分区 7 的待建标记，附件（材料） → 分区 7 的输入登记，
 * 变更记录 → 分区 9 的版本历史（真数据替换了占位）。
 */
type TabKey = 'card' | 'reg' | 'def' | 'sop' | 'cap' | 'ppl' | 'data' | 'runs' | 'bp';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'card', label: 'ATC 任务卡' },
  { key: 'reg', label: 'Registration · 边界' },
  { key: 'def', label: '定义 · 四段（工作流）' },
  { key: 'sop', label: 'SOP 全流程' },
  { key: 'cap', label: 'ACTION 包与调用登记' },
  { key: 'ppl', label: '人员与责任' },
  { key: 'data', label: '数据链接' },
  { key: 'runs', label: '执行台账' },
  { key: 'bp', label: '蓝图与版本' },
];

/** 生命周期七态链。取值与库级 CHECK 同序，中文用全仓统一译名。 */
const STATUS_CHAIN = atcStatusSchema.options;

/*
 * 二维码：三个定位角 + 中间几个点。
 *
 * 画三个角而不是画满一整张码：这是个占位，不是真码——真码在等短码方案裁定。
 * 三个角是二维码唯一能一眼认出来的特征，而画满的假码会让人以为它已经能扫了。
 */
const QR_ICON = (
  <svg width="26" height="26" viewBox="0 0 14 14" aria-hidden="true">
    <g fill="none" stroke="currentColor" strokeWidth="1.2">
      <rect x="1.2" y="1.2" width="4" height="4" rx="0.6" />
      <rect x="8.8" y="1.2" width="4" height="4" rx="0.6" />
      <rect x="1.2" y="8.8" width="4" height="4" rx="0.6" />
    </g>
    <g fill="currentColor">
      <rect x="8.8" y="8.8" width="1.6" height="1.6" />
      <rect x="11.2" y="11.2" width="1.6" height="1.6" />
    </g>
  </svg>
);

/** 草稿态的非概览分区：还没落库，各分区的数据都无从谈起。 */
function DraftPending(): ReactNode {
  return (
    <div className="pending">
      <span className="eyebrow">还没保存</span>
      <p>这张卡还没落库——先保存，各分区才有内容可读。</p>
    </div>
  );
}

export function AtcDetail({
  index,
  subject,
  rootName,
  canManage,
  onChanged,
  onCreated,
  onCancelDraft,
  onSelect,
  startEditing = false,
}: {
  index: TreeIndex;
  subject: AtcSubject;
  rootName: string;
  canManage: boolean;
  /** 名称或状态变了，外层重新拉树——树上和卡片上都写着它们。 */
  onChanged: () => void;
  /** 草稿落库了。外层跳到它的详情页——刚建好的那一份就是接下来要填内容的。 */
  onCreated: (node: TreeNode) => void;
  /** 放弃这份草稿。 */
  onCancelDraft: () => void;
  /** 卡头归属面包屑的跳转：点部门/岗位，树上走到那个节点。 */
  onSelect: (id: string) => void;
  /** 台账「修改」跳转进来时直接进入编辑态（C-190 批三 A8）。 */
  startEditing?: boolean;
}): ReactNode {
  const draft = subject.kind === 'draft' ? subject : null;
  const node = subject.kind === 'saved' ? subject.node : null;

  const [tab, setTab] = useState<TabKey>('card');

  /**
   * 建卡时这张卡是运营还是建设。
   *
   * **初值是 null，不是「运营」**——库与契约都要求必填且无默认，
   * 界面给个默认就等于替人做了这个决定，而这个轴的全部价值就在于建卡时想清楚。
   * 没选就存不下去（下面的 `save()` 会拦）。
   */
  const [cls, setCls] = useState<'operational' | 'construction' | null>(null);

  /** 已落库那一份的编码。草稿没有编码——它还没有身份，也就没有执行记录可查。 */
  const savedCode = node?.code ?? null;

  /*
   * 卡页的数据分六路，按分区需要拉取：
   *
   * - standard（读模型）：九个分区里六个要它，打开就拉；
   * - versions（版本历史）：分区 1/2/3/5/9 要它，打开就拉；
   * - runs（执行台账）：分区 1 的计量与分区 8 的台账，两页都到时拉；
   * - people（人员台账）：分区 1 的责任人行与分区 6；
   * - inputs（输入登记）：分区 7；
   * - standards（依据标准）：分区 2。
   *
   * 拉不到就保持 null，面板显示「读取中……」——不编一个「没有」的结论。
   */
  const [standard, setStandard] = useState<AtcStandard | null>(null);
  const [versions, setVersions] = useState<AtcVersion[] | null>(null);
  useEffect(() => {
    if (savedCode === null) return undefined;
    let live = true;
    void api.execStandard(savedCode).then(
      (data) => live && setStandard(data),
      () => undefined,
    );
    void api.atcVersions(savedCode).then(
      (data) => live && setVersions(data.versions),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [savedCode]);

  const [runs, setRuns] = useState<RunLedgerRow[] | null>(null);
  useEffect(() => {
    if ((tab !== 'card' && tab !== 'runs') || savedCode === null) return undefined;
    let live = true;
    void api.execRuns(savedCode).then(
      (data) => live && setRuns(data.rows),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [tab, savedCode]);

  const [people, setPeople] = useState<AtcPersonList | null>(null);
  useEffect(() => {
    // 卡头的负责人/执行者行使卡页一打开就需要它——不再等切到具体分区
    if (savedCode === null) return undefined;
    let live = true;
    void api.atcCardPeople(savedCode).then(
      (data) => live && setPeople(data),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [savedCode]);

  const [inputs, setInputs] = useState<AtcInput[] | null>(null);
  useEffect(() => {
    if (tab !== 'data' || savedCode === null) return undefined;
    let live = true;
    void api.execInputs(savedCode).then(
      (data) => live && setInputs(data.rows),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [tab, savedCode]);

  const [standards, setStandards] = useState<AtcStandardList | null>(null);
  useEffect(() => {
    if (tab !== 'reg' || savedCode === null) return undefined;
    let live = true;
    void api.atcStandards(savedCode).then(
      (data) => live && setStandards(data),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [tab, savedCode]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState(node?.name ?? '');
  const [typeCode, setTypeCode] = useState(draft?.options[0]?.code ?? node?.typeCode ?? '');

  /*
   * 编辑态（C-190 批二 A6）：点「编辑」→ 分区 1 的资产信息变表单。
   * 可改五项：名称、描述（字段未建，禁用位）、触发类型、触发说明、成熟度。
   * 只读三项：编号、归属、人员。**四段、TEST、基线不在其中**——四段走评审流程。
   * 名称不再在标题上就地改：标题是标题，改是编辑态的事——一个入口。
   */
  const [editing, setEditing] = useState(startEditing && node !== null && canManage);
  const [editName, setEditName] = useState(node?.name ?? '');
  const [editTriggerType, setEditTriggerType] = useState<string>('');
  const [editTriggerNote, setEditTriggerNote] = useState('');
  const [editMaturity, setEditMaturity] = useState<string>('');

  /**
   * 名字改过了没有（**只服务草稿**）。已建的卡改名走编辑态——标题是标题，
   * 改是编辑的事，同一个字段不留两个入口。
   */
  const dirty = draft !== null && name.trim() !== '';

  /** 当前版本。版本历史里有 isCurrent 标记，不在前端猜「最新的一版」。 */
  const currentVersion = versions?.find((v) => v.isCurrent) ?? null;

  /**
   * 进入编辑态：表单初值从当前版本与本体取。
   * 触发说明落在 `trigger_spec.note`（trigger_spec 是自由 jsonb 区，note 是这里定的键）。
   */
  function enterEdit(): void {
    if (node === null || standard === null) return;
    setEditName(node.name);
    setEditTriggerType(currentVersion?.triggerType ?? 'undetermined');
    const spec = currentVersion?.triggerSpec;
    setEditTriggerNote(
      typeof spec === 'object' && spec !== null && 'note' in spec
        ? String((spec as { note: unknown }).note)
        : '',
    );
    setEditMaturity(standard.maturityLevel);
    setError(null);
    setEditing(true);
  }

  /**
   * 编辑态保存。**五个字段分两条路写**，因为它们的档位不同：
   * - 名称、成熟度 → `atc` 本体（PATCH）
   * - 触发类型、触发说明 → 版本档（`atc_version`）：改触发就是改定义，
   *   提一版新的（四段沿用当前版预填），历史执行仍指着当时那版
   * 描述不在其中——那个字段还没建（待建），表单里它是禁用位。
   */
  async function saveEdit(): Promise<void> {
    if (node === null || standard === null || currentVersion == null) return;
    if (editName.trim() === '') {
      setError('名称不能为空');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (editName.trim() !== node.name || editMaturity !== standard.maturityLevel) {
        await api.updateAtc(node.id, {
          ...(editName.trim() !== node.name ? { name: editName.trim() } : {}),
          ...(editMaturity !== standard.maturityLevel
            ? { maturityLevel: editMaturity as 'L0' | 'L1' | 'L2' | 'L3' | 'L4' | 'L5' }
            : {}),
        });
      }
      const oldSpec = currentVersion.triggerSpec;
      const oldNote =
        typeof oldSpec === 'object' && oldSpec !== null && 'note' in oldSpec
          ? String((oldSpec as { note: unknown }).note)
          : '';
      if (editTriggerType !== currentVersion.triggerType || editTriggerNote !== oldNote) {
        await api.addAtcVersion(standard.code, {
          what: currentVersion.what,
          how: currentVersion.how,
          actionSpec: currentVersion.actionSpec as Record<string, unknown>,
          testSpec: currentVersion.testSpec as Record<string, unknown>,
          triggerType: editTriggerType as 'schedule' | 'event' | 'manual' | 'undetermined',
          triggerSpec: {
            ...(typeof oldSpec === 'object' && oldSpec !== null
              ? (oldSpec as Record<string, unknown>)
              : {}),
            note: editTriggerNote,
          },
          hotlGate: currentVersion.hotlGate,
          determinism: currentVersion.determinism as 'deterministic' | 'exploratory',
          authoredBy: 'human',
        });
      }
      setEditing(false);
      onChanged();
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  }

  /**
   * 停用 / 启用。
   *
   * **ATC 没有删除**：执行记录要引用它依据的那一份定义，删掉之后那些记录就指向
   * 一个不存在的东西。状态用 `statusOf(node)` 判，不用 `node.enabled`（投影只有两档）。
   * 这一页只提供 active ↔ suspended 这一对可逆迁移；`retired` 是终态，不给入口。
   *
   * 不给二次确认：停用是可逆的，拦一道只是多一次点击。
   */
  async function setSuspended(suspend: boolean): Promise<void> {
    if (node === null) return;
    setBusy(true);
    setError(null);
    try {
      // 状态迁移走专门入口，不走 PATCH——那一处带着状态机校验与留痕。
      await api.changeAtcStatus(
        node.id,
        suspend ? 'suspended' : 'active',
        suspend ? '在卡页停用' : '在卡页启用',
      );
      onChanged();
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  }

  /**
   * 保存。**只在草稿态存在**——已建的卡改名/改属性走编辑态（saveEdit）。
   *
   * **建卡直写 `atc` 表**（`/api/org/atcs`）。挂载点是 `positionId`：
   * **ATC 只挂岗位**，库级 NOT NULL 外键保这一条，界面上因此没有「挂到哪儿」这个自由选择。
   */
  async function save(): Promise<void> {
    if (name.trim() === '') {
      setError('给这份标准起个名字');
      return;
    }
    if (draft === null) return;

    setBusy(true);
    setError(null);
    try {
      // 编码不传：服务端按规则再算一遍并以它为准——中间有人抢先建了，号往后挪一位。
      if (draft.parentId === null) {
        setError('ATC 必须挂在一个岗位下——请先选岗位再建卡');
        return;
      }
      if (cls === null) {
        setError('先选这张卡是运营 ATC 还是建设 ATC——两类的建设路径不同，建卡时就要定');
        return;
      }
      const saved = await api.addAtc({ positionId: draft.parentId, name: name.trim(), cls });
      // 树是投影，建完重新拉一次；本地拼不出 sortOrder 与 attributes。
      onCreated({
        id: saved.id,
        typeCode: 'ATC',
        parentId: saved.positionId,
        name: saved.name,
        code: saved.code,
        sortOrder: 0,
        enabled: true,
        attributes: { status: saved.status, ownerScope: saved.ownerScope },
        memberCount: 0,
      });
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  }

  /*
   * 类型、归属：草稿也算得出来。编码**不算**——`nextSequence`
   * 扫的是浏览器手里这棵树，一个人单独操作也能和库里的发号器对不上；
   * 预览一个可能错的号，比不预览更坏。草稿态只显示「保存时自动生成」。
   */
  const ownerOf = draft === null ? node!.id : (draft.parentId ?? ROOT_ID);
  const owner =
    draft === null
      ? owningUnitOf(index, ownerOf)
      : ownerOf === ROOT_ID
        ? null
        : // 草稿挂在这个节点下面，所以归属就从这个节点自己开始找（它自己可能就是）
          (index.byId.get(ownerOf) ?? null);
  const ownerPath =
    owner === null ? null : pathNamesOf(index, owner.id, rootName).slice(1).join(' / ');

  const code = draft === null ? node!.code : null;

  /** 生命周期状态：读 attributes.status（库里那个字符串），不按 enabled 两档猜。 */
  const status = node === null ? null : statusOf(node);

  /*
   * 归属面包屑的节点链：根（企业）→ … → 责任岗位。可点——点了树上走到那一层。
   * 从卡自己沿 parentId 上溯；卡不是自己的归属，所以不含它。
   */
  const crumbs: { id: string; name: string }[] = [{ id: ROOT_ID, name: rootName }];
  if (node !== null) {
    const chain: { id: string; name: string }[] = [];
    let cursor = node.parentId;
    while (cursor !== null) {
      const up = index.byId.get(cursor);
      if (up === undefined) break;
      chain.unshift({ id: up.id, name: up.name });
      cursor = up.parentId;
    }
    crumbs.push(...chain);
  }

  /*
   * 自定义字段：attributes 是节点的扩展点，但其中有三个键是**系统属性**——
   * status（生命周期，状态链那一格就是它）、cls（运营/建设）、ownerScope
   * （客户的卡/我方的卡）。它们各有专门的呈现位置，再列一遍就是同一事实两处显示。
   */
  const SYSTEM_ATTRS = new Set(['status', 'cls', 'ownerScope']);
  const attributes = Object.entries(node?.attributes ?? {}).filter(
    ([key]) => !SYSTEM_ATTRS.has(key),
  );

  /*
   * 编辑态表单（A6）：可改名称、描述（待建禁用位）、触发类型、触发说明、成熟度；
   * 只读编号、归属、人员。**四段、TEST、基线不在其中**——四段走评审流程。
   */
  const editForm = (
    <div className="blk">
      <h3>
        资产信息 <span className="sub">编辑态 · 保存即留痕</span>
      </h3>
      <div className="frm2">
        <Field id="e-name" label="名称">
          <input
            value={editName}
            onChange={(event) => setEditName(event.target.value)}
            aria-label="名称"
          />
        </Field>
        <Field id="e-desc" label="描述" aux="待建">
          <input
            disabled
            value=""
            placeholder="长期的业务说明——字段尚未建（本体档），建好后在此可改"
            aria-label="描述"
          />
        </Field>
        <Field id="e-trig" label="触发类型">
          <select
            value={editTriggerType}
            aria-label="触发类型"
            onChange={(event) => setEditTriggerType(event.target.value)}
          >
            {(['schedule', 'event', 'manual', 'undetermined'] as const).map((t) => (
              <option key={t} value={t}>
                {TRIGGER_LABEL[t]}
              </option>
            ))}
          </select>
        </Field>
        <Field id="e-trignote" label="触发说明">
          <input
            value={editTriggerNote}
            onChange={(event) => setEditTriggerNote(event.target.value)}
            aria-label="触发说明"
          />
        </Field>
        <Field id="e-mat" label="成熟度">
          <select
            value={editMaturity}
            aria-label="成熟度"
            onChange={(event) => setEditMaturity(event.target.value)}
          >
            {(['L0', 'L1', 'L2', 'L3', 'L4', 'L5'] as const).map((m) => (
              <option key={m} value={m}>
                {MATURITY_LABEL[m]}
              </option>
            ))}
          </select>
        </Field>
        <dl className="kv">
          <dt>编号</dt>
          <dd className="mono">{code ?? '未填'}</dd>
          <dt>归属</dt>
          <dd>{ownerPath ?? `${rootName}（全公司通用）`}</dd>
          <dt>负责人</dt>
          <dd>
            {people === null ? (
              '读取中……'
            ) : people.owner === null ? (
              <span className="zero">尚未指派负责人</span>
            ) : (
              `${people.owner.name}${people.owner.positionName === null ? '' : `（${people.owner.positionName}）`}`
            )}
          </dd>
        </dl>
      </div>
      <div className="head-actions" style={{ marginTop: 10 }}>
        <BusyButton busy={busy} busyLabel="保存中" type="button" onClick={() => void saveEdit()}>
          保存修改
        </BusyButton>
        <button type="button" className="btn quiet" onClick={() => setEditing(false)}>
          取消
        </button>
      </div>
      <p className="sub-hint">
        归属变更不在此改——用「复制」建副本挂新归属，原卡停用即可，历史台账保留。
        四段与 TEST 修订走评审流程。
      </p>
    </div>
  );

  return (
    <article className="atc">
      <header className="atc-head">
        {/*
          副标题四个字，**其后没有解释段落**。
          原型那段「Agentic Task Card——脑力劳动的载体……」整段不搬：
          产品页面标识对象，不解释对象。要解释的话另有一页。
        */}
        <p className="atc-kind">ATC 任务卡</p>

        <div className="atc-title-row">
          {/*
            标题行（原型 .ahd .r1 :133）：名称 + 编码 + 归属面包屑 + 动作。
            已建的卡标题是 h1——改名在编辑态（同一字段不留两个入口）。
            草稿是输入框：创建时它是这一页唯一要填的东西。
          */}
          {draft !== null ? (
            <input
              className="atc-name"
              value={name}
              placeholder="给这份标准起个名字"
              aria-label="名称"
              autoFocus
              onChange={(event) => setName(event.target.value)}
            />
          ) : (
            <h1>{name}</h1>
          )}

          {/* 编码不占一行——跟在标题后，等宽灰字（原型 :135）。草稿不出具体号。 */}
          {draft !== null ? (
            <span className="atc-code">编号将在保存时自动生成</span>
          ) : (
            <span className={`atc-code${code === null ? '' : ' mono'}`}>{code ?? '未填'}</span>
          )}

          {/*
            归属是可点的面包屑（原型 :136 的 path pill），点了树上走到那一层。
            草稿点了会丢掉正在填的名字——草稿的归属在下方元信息里纯文本呈现。
          */}
          {draft === null && (
            <span className="atc-crumbs">
              {crumbs.map((c, i) => (
                <span key={c.id}>
                  {i > 0 && <span className="atc-crumb-sep">/</span>}
                  <button type="button" className="crumb-btn" onClick={() => onSelect(c.id)}>
                    {c.name}
                  </button>
                </span>
              ))}
            </span>
          )}

          <div className="head-actions">
            {/* 保存只服务草稿（创建）。已建的卡保存编辑在编辑态里。 */}
            {canManage && draft !== null && dirty && (
              <BusyButton busy={busy} busyLabel="保存中" type="button" onClick={() => void save()}>
                创建
              </BusyButton>
            )}

            {/*
              编辑：资产信息的修改入口（C-190 批二 A6）。读模型没回来之前不开放——
              表单的初值要从当前版本取，空初值会让人把没读到当成没有。
            */}
            {canManage && draft === null && !editing && (
              <button
                type="button"
                className="btn quiet"
                disabled={standard === null}
                title={standard === null ? '读取中，稍后' : '修改资产信息'}
                onClick={enterEdit}
              >
                ✎ 编辑
              </button>
            )}
            {canManage && draft === null && editing && (
              <button type="button" className="btn quiet" onClick={() => setEditing(false)}>
                取消编辑
              </button>
            )}

            {/*
              复制：留位待裁。副本的语义（新卡还是引用、TEST 要不要复验）尚未裁定。
              与其他按钮同尺寸的禁用态 + 悬停提示——留位的意思是留一个位置，
              不是留一个洞。
            */}
            {canManage && draft === null && (
              <button
                type="button"
                className="btn quiet"
                disabled
                title="留位待裁——复制此卡到其他归属：副本语义尚未裁定"
              >
                ⧉ 复制
              </button>
            )}

            {/*
              二维码：这一页对外的出口。留位待裁——码内容的方案（编码还是短码）
              尚未裁定，裁定前不印假码。同样是按钮尺寸的禁用态。

              草稿不给：还没落库，没有身份，也没有可贴的东西。
            */}
            {node !== null && (
              <button
                type="button"
                className="btn quiet"
                disabled
                aria-label="二维码"
                title="留位待裁——二维码出口：码内容的方案尚未裁定"
              >
                {QR_ICON}
              </button>
            )}

            {/*
              停用 / 启用：active ↔ suspended 这一对可逆迁移。
              retired 是终态——终态不给「取消」入口，给了就等于说终态不终。
            */}
            {canManage && draft === null && status === 'active' && (
              <BusyButton
                busy={busy}
                busyLabel="停用中"
                type="button"
                className="btn quiet"
                onClick={() => void setSuspended(true)}
              >
                停用
              </BusyButton>
            )}
            {canManage && draft === null && status === 'suspended' && (
              <BusyButton
                busy={busy}
                busyLabel="启用中"
                type="button"
                className="btn quiet"
                onClick={() => void setSuspended(false)}
              >
                启用
              </BusyButton>
            )}
            {canManage && draft !== null && (
              <button className="btn quiet" type="button" onClick={onCancelDraft}>
                取消
              </button>
            )}
          </div>
        </div>

        {error !== null && <p className="msg">{error}</p>}

        {/*
          建卡时选一类。**只在草稿态出现**：已建的卡要改类别走改卡，不在标题行上改。
          两个按钮而不是下拉：只有两个值，下拉要多点一次，且合起来时看不见另一个值。
        */}
        {draft !== null && (
          <div className="cls-pick" role="group" aria-label="运营 ATC 还是建设 ATC">
            {(['operational', 'construction'] as const).map((value) => (
              <button
                key={value}
                type="button"
                className={cls === value ? 'btn' : 'btn quiet'}
                aria-pressed={cls === value}
                onClick={() => setCls(value)}
              >
                {ATC_CLASS_LABEL[value]}
              </button>
            ))}
          </div>
        )}

        {/*
          属性徽标行（原型 atcHTML 1530-1536）：类别 ｜ 确定性 ｜ 工作流步数 ｜
          须人工审批 ｜ 成熟度五格条。这几个属性上了卡头，「资产信息」里就不再重复——
          同一个事实在一页上出现两次，是 SSOT 在呈现层的破口。
        */}
        {node !== null && <CardBadges cls={atcClassOf(node)} standard={standard} />}

        {/*
          负责人与执行者（原型 .own :260-263；C-190 批二 A5）：
          负责人唯一、突出——它是本体档（atc.owner_person_no），决策找他；
          执行者是使用者（role='user'），最多摆两个，其余折叠成 +N，点了去「人员与责任」。
          那句规则说明保留——它表达的正是 principal 那一层。
        */}
        {node !== null && (
          <div className="own-row">
            <span className="own-label">负责人</span>
            {people === null ? (
              <span className="sub-hint">读取中……</span>
            ) : people.owner === null ? (
              <span className="zero">尚未指派负责人</span>
            ) : (
              <span className="oc">
                {people.owner.name}
                {people.owner.positionName !== null && `（${people.owner.positionName}）`}
                {people.ownerInferred && <span className="sub-hint">（回填推断）</span>}
              </span>
            )}
            <span className="own-label">执行者</span>
            {(() => {
              const users = people?.rows.filter((r) => r.role === 'user' && r.enabled) ?? [];
              const shown = users.slice(0, 2);
              const rest = users.length - shown.length;
              return (
                <>
                  {people === null ? (
                    <span className="sub-hint">读取中……</span>
                  ) : users.length === 0 ? (
                    <span className="zero">岗位成员默认即使用者</span>
                  ) : (
                    shown.map((u) => (
                      <span key={u.atcPersonId} className="oc u">
                        {u.name}
                      </span>
                    ))
                  )}
                  {rest > 0 && (
                    <button type="button" className="crumb-btn" onClick={() => setTab('ppl')}>
                      +{rest}
                    </button>
                  )}
                </>
              );
            })()}
            <span className="sub-hint">每次执行落一名责任人——谁调用谁负责</span>
          </div>
        )}

        {/*
          生命周期七态链：当前格高亮，走过的格淡色。状态不单列——状态链本身就表达了状态。
          七态一个不能少——少了「待建」，入池待建的卡就在链上找不到自己的位置。
        */}
        {node !== null && status !== null && (
          <div className="status-chain" aria-label="生命周期">
            {STATUS_CHAIN.map((s, i) => (
              <span
                key={s}
                className={
                  s === status
                    ? 'cur'
                    : i < STATUS_CHAIN.indexOf(status as AtcStatus)
                      ? 'past'
                      : ''
                }
              >
                {ATC_STATUS_LABEL[s]}
              </span>
            ))}
          </div>
        )}

        {/*
          草稿的元信息行（已建的卡没有这一行：编码在标题后、归属是面包屑、
          状态由状态链表达——同一事实不在一页上出现两次）。
        */}
        {draft !== null && (
          <div className="meta-row">
            <dl className="meta">
              <dt>归属</dt>
              {/* 挂在企业下的标准归企业本身，不显示「归 X」——那时候没有 X。 */}
              <dd>{ownerPath ?? `${rootName}（全公司通用）`}</dd>
            </dl>

            <dl className="meta">
              <dt>状态</dt>
              <dd>
                <span className="chip wait">还没保存</span>
              </dd>
            </dl>

            {/* 草稿态还能选类型（存过之后类型不能改），所以这一格只在草稿态出现。 */}
            {draft.options.length > 1 && (
              <dl className="meta">
                <dt>类型</dt>
                <dd>
                  <select
                    value={typeCode}
                    aria-label="类型"
                    onChange={(event) => setTypeCode(event.target.value)}
                  >
                    {draft.options.map((option) => (
                      <option key={option.code} value={option.code}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                </dd>
              </dl>
            )}
          </div>
        )}
      </header>

      <div className="tabline scroll stabs" role="tablist" aria-label="ATC 分区">
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={tab === item.key}
            onClick={() => setTab(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* 分区 1 · ATC 任务卡（概览）。草稿态只有这里有事做：把名字定了、把卡落库。 */}
      {tab === 'card' && (
        <div className="atc-panel">
          {draft !== null ? (
            <div className="blk">
              <h3>
                资产信息 <span className="sub">先落库，其余分区才有内容可读</span>
              </h3>
              <dl className="kv">
                <dt>编码</dt>
                <dd>编号将在保存时自动生成</dd>
                <dt>归属</dt>
                <dd>{ownerPath ?? `${rootName}（全公司通用）`}</dd>
                <dt>状态</dt>
                <dd>还没保存</dd>
              </dl>
              <p className="sub-hint">
                先给这份标准起名字、选定运营还是建设，再创建。落库之后，
                四段定义、边界、人员、数据链接各分区才有内容可填可读。
              </p>
            </div>
          ) : (
            <OverviewPanel
              standard={standard}
              currentVersion={currentVersion}
              runs={runs}
              people={people}
              infoOverride={editing ? editForm : undefined}
            />
          )}

          {savedCode !== null && (
            <div className="blk">
              <h3>
                基线对照 <span className="sub">分母（改造前人工）对各版实测均耗——改造成效在此读出</span>
              </h3>
              {/*
                基线是改造成效的分母：改造前人做一次要多久、多久做一次。
                但分母单独摆着读不出成效——分子（各版实测均耗）也要在，
                所以这一块的默认态是**对照**，不是表单。
                录/改分母点「✎ 编辑」进表单态；表单本身一行未改，
                「已有基线改动必填修订理由」的拦截仍在它里面。
              */}
              <AtcBaselineBlock
                code={savedCode}
                canManage={canManage}
                currentVersionNo={currentVersion?.versionNo ?? null}
                onChanged={onChanged}
              />
            </div>
          )}

          {node !== null && (
            <div className="blk">
              <h3>
                自定义字段 <span className="sub">节点自带的扩展点</span>
              </h3>
              {attributes.length === 0 ? (
                <p className="sub-hint">
                  这份标准上还没有自定义字段。attributes 是节点自带的扩展点，
                  触发条件、预计工时、资质要求这类字段定下来之后落在这儿。
                </p>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>字段</th>
                        <th>值</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attributes.map(([key, value]) => (
                        <tr key={key}>
                          <td>{key}</td>
                          <td className="mono">
                            {typeof value === 'string' ? value : JSON.stringify(value)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'reg' && (
        <div className="atc-panel">
          {draft !== null ? (
            <DraftPending />
          ) : (
            <RegistrationPanel
              standard={standard}
              currentVersion={currentVersion}
              standards={standards}
            />
          )}
        </div>
      )}

      {tab === 'def' && (
        <div className="atc-panel">
          {draft !== null ? (
            <DraftPending />
          ) : (
            <DefinitionPanel standard={standard} currentVersion={currentVersion} />
          )}
        </div>
      )}

      {tab === 'sop' && (
        <div className="atc-panel">
          {draft !== null ? <DraftPending /> : <SopPanel standard={standard} />}
        </div>
      )}

      {tab === 'cap' && (
        <div className="atc-panel">
          {draft !== null ? (
            <DraftPending />
          ) : (
            <ActionPackagePanel currentVersion={currentVersion} />
          )}
        </div>
      )}

      {tab === 'ppl' && (
        <div className="atc-panel">
          {draft !== null ? <DraftPending /> : <PeoplePanel people={people} />}
        </div>
      )}

      {tab === 'data' && (
        <div className="atc-panel">
          {draft !== null ? (
            <DraftPending />
          ) : (
            <DataLinksPanel standard={standard} inputs={inputs} />
          )}
        </div>
      )}

      {tab === 'runs' && (
        <div className="atc-panel">
          {draft !== null ? <DraftPending /> : <RunsPanel runs={runs} />}
        </div>
      )}

      {tab === 'bp' && (
        <div className="atc-panel">
          {draft !== null ? (
            <DraftPending />
          ) : (
            <>
              <BlueprintPanel standard={standard} />
              <div className="blk">
                <h3>
                  版本历史 <span className="sub">旧版永不覆盖——历史执行锁定当时那一版</span>
                </h3>
                {versions === null ? (
                  <p className="sub-hint">读取中……</p>
                ) : (
                  <AtcVersionHistory versions={versions} />
                )}
              </div>
            </>
          )}
        </div>
      )}
    </article>
  );
}
